import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicHeader from '../../components/navigation/PublicHeader';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import RequestCard from './components/RequestCard';
import FilterPanel from './components/FilterPanel';

const ServiceRequestHistory = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    urgency: 'all',
    issueType: 'all',
    dateRange: 'all'
  });
  const [driverPhone, setDriverPhone] = useState(null);

  useEffect(() => {
    // Get driver phone from localStorage (saved from driver lookup)
    const savedPhone = localStorage.getItem('currentDriverPhone');
    if (!savedPhone) {
      // If no phone found, redirect to driver lookup
      navigate('/');
      return;
    }
    setDriverPhone(savedPhone);
    fetchServiceRequests(savedPhone);
  }, [navigate]);

  useEffect(() => {
    applyFilters();
  }, [requests, searchQuery, filters]);

  const fetchServiceRequests = async (phone) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase?.from('service_requests')?.select('*')?.eq('driver_phone', phone)?.order('created_at', { ascending: false });

      if (fetchError) {
        console.error('Fetch error:', fetchError);
        setError('Failed to load service requests. Please try again.');
        return;
      }

      setRequests(data || []);
    } catch (err) {
      console.error('Error:', err);
      setError('An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...requests];

    // Search filter
    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(req => 
        req?.request_number?.toLowerCase()?.includes(query) ||
        req?.issue_type?.toLowerCase()?.includes(query) ||
        req?.description?.toLowerCase()?.includes(query)
      );
    }

    // Status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(req => req?.status === filters?.status);
    }

    // Urgency filter
    if (filters?.urgency !== 'all') {
      filtered = filtered?.filter(req => req?.urgency === filters?.urgency);
    }

    // Issue type filter
    if (filters?.issueType !== 'all') {
      filtered = filtered?.filter(req => req?.issue_type === filters?.issueType);
    }

    // Date range filter
    if (filters?.dateRange !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (filters?.dateRange) {
        case 'today':
          filterDate?.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate?.setDate(now?.getDate() - 7);
          break;
        case 'month':
          filterDate?.setMonth(now?.getMonth() - 1);
          break;
        case '3months':
          filterDate?.setMonth(now?.getMonth() - 3);
          break;
      }

      filtered = filtered?.filter(req => new Date(req?.created_at) >= filterDate);
    }

    setFilteredRequests(filtered);
  };

  const handleRefresh = () => {
    if (driverPhone) {
      fetchServiceRequests(driverPhone);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'all',
      urgency: 'all',
      issueType: 'all',
      dateRange: 'all'
    });
    setSearchQuery('');
  };

  const getStatusCounts = () => {
    return {
      total: requests?.length || 0,
      pending: requests?.filter(r => r?.status === 'pending')?.length || 0,
      inProgress: requests?.filter(r => r?.status === 'in-progress')?.length || 0,
      completed: requests?.filter(r => r?.status === 'completed')?.length || 0
    };
  };

  const statusCounts = getStatusCounts();

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <main className="pt-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-7xl mx-auto">
            {/* Header Section */}
            <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1 mb-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h1 className="text-2xl font-semibold text-foreground mb-2">Service Request History</h1>
                  <p className="text-sm text-muted-foreground">
                    Track your submitted service requests and their current status
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="RefreshCw"
                    iconPosition="left"
                    onClick={handleRefresh}
                    disabled={isLoading}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => navigate('/')}
                  >
                    New Request
                  </Button>
                </div>
              </div>

              {/* Status Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="FileText" size={16} color="var(--color-primary)" strokeWidth={2} />
                    <span className="text-xs text-muted-foreground">Total</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">{statusCounts?.total}</p>
                </div>
                <div className="bg-warning/10 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Clock" size={16} color="var(--color-warning)" strokeWidth={2} />
                    <span className="text-xs text-muted-foreground">Pending</span>
                  </div>
                  <p className="text-2xl font-bold text-warning">{statusCounts?.pending}</p>
                </div>
                <div className="bg-primary/10 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Activity" size={16} color="var(--color-primary)" strokeWidth={2} />
                    <span className="text-xs text-muted-foreground">In Progress</span>
                  </div>
                  <p className="text-2xl font-bold text-primary">{statusCounts?.inProgress}</p>
                </div>
                <div className="bg-success/10 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="CheckCircle2" size={16} color="var(--color-success)" strokeWidth={2} />
                    <span className="text-xs text-muted-foreground">Completed</span>
                  </div>
                  <p className="text-2xl font-bold text-success">{statusCounts?.completed}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
              {/* Filter Sidebar */}
              <div className="lg:w-80">
                <FilterPanel
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={handleClearFilters}
                />
              </div>

              {/* Main Content */}
              <div className="flex-1 space-y-6">
                {/* Search Bar */}
                <div className="bg-card border border-border rounded-lg p-4 shadow-elevation-1">
                  <div className="relative">
                    <Icon
                      name="Search"
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      strokeWidth={2}
                    />
                    <Input
                      type="text"
                      placeholder="Search by request ID, issue type, or description..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Loading State */}
                {isLoading && (
                  <div className="bg-card border border-border rounded-lg p-12 text-center">
                    <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mx-auto mb-4 animate-pulse">
                      <Icon name="Loader2" size={32} color="var(--color-primary)" strokeWidth={2} className="animate-spin" />
                    </div>
                    <p className="text-sm text-muted-foreground">Loading service requests...</p>
                  </div>
                )}

                {/* Error State */}
                {!isLoading && error && (
                  <div className="bg-error/10 border border-error/20 rounded-lg p-6 text-center">
                    <Icon name="AlertCircle" size={48} color="var(--color-error)" strokeWidth={2} className="mx-auto mb-4" />
                    <p className="text-sm text-error mb-4">{error}</p>
                    <Button variant="outline" size="sm" onClick={handleRefresh}>
                      Try Again
                    </Button>
                  </div>
                )}

                {/* Empty State */}
                {!isLoading && !error && filteredRequests?.length === 0 && requests?.length === 0 && (
                  <div className="bg-card border border-border rounded-lg p-12 text-center">
                    <div className="flex items-center justify-center w-16 h-16 bg-muted/30 rounded-full mx-auto mb-4">
                      <Icon name="FileText" size={32} color="var(--color-muted-foreground)" strokeWidth={2} />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">No Service Requests Yet</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      You haven't submitted any service requests. Start by creating your first request.
                    </p>
                    <Button
                      variant="default"
                      size="sm"
                      iconName="Plus"
                      iconPosition="left"
                      onClick={() => navigate('/')}
                    >
                      Create Service Request
                    </Button>
                  </div>
                )}

                {/* No Results State */}
                {!isLoading && !error && filteredRequests?.length === 0 && requests?.length > 0 && (
                  <div className="bg-card border border-border rounded-lg p-12 text-center">
                    <Icon name="Search" size={48} color="var(--color-muted-foreground)" strokeWidth={2} className="mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">No Matching Requests</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      No service requests match your current filters or search query.
                    </p>
                    <Button variant="outline" size="sm" onClick={handleClearFilters}>
                      Clear Filters
                    </Button>
                  </div>
                )}

                {/* Request Cards */}
                {!isLoading && !error && filteredRequests?.length > 0 && (
                  <div className="space-y-4">
                    {filteredRequests?.map((request) => (
                      <RequestCard key={request?.id} request={request} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ServiceRequestHistory;